import {
  Championship,
  GameInfoMapped,
  Match,
  Member,
  Organizer,
  Team,
  Teams,
} from 'modules/overlay-v2/domain/interfaces/game-info/gameInfoMapped';

import { ChampionshipStats } from '../interfaces/championship-stats';
import { IGameInfoDto } from 'modules/overlay-v2/domain/interfaces/game-info/game-info-dto';
import { format } from 'date-fns';
import { getCityFromAddress } from 'shared/utils';
import ru from 'date-fns/locale/ru';
import { USER_DEFAULT_AVATAR } from 'shared/constants/links';

export function gameInfoDataMapper(gameInfo: IGameInfoDto): GameInfoMapped {
  return {
    match: extractMatch(gameInfo),
    teams: extractTeams(gameInfo),
    organizer: extractOrganizer(gameInfo),
    championship: extractChampionship(gameInfo),
  };
}

function extractMatch(gameInfo: IGameInfoDto): Match {
  const date = gameInfo?.date ? new Date(gameInfo?.date) : undefined;
  const location = gameInfo?.location;
  const tournament = gameInfo?.tournament;

  return {
    id: gameInfo.id,
    dateInfo: {
      fullDate: gameInfo.date,
      date: date ? format(date, 'd MMMM', { locale: ru }) : 'Неизвестная дата',
      time: date ? format(date, 'HH:mm') : 'Неизвестное время',
    },
    address: {
      city: getCityFromAddress(location?.address) ?? getCityFromAddress(tournament?.address) ?? 'Неизвестный город',
      country: location?.address?.country,
      region: location?.address?.region,
    },
  };
}

function extractTeams(gameInfo: IGameInfoDto): Teams {
  return {
    firstTeam: extractTeam(gameInfo, 0),
    secondTeam: extractTeam(gameInfo, 1),
  };
}

function extractTeam(gameInfo: IGameInfoDto, teamIndex: number): Team | undefined {
  if (!gameInfo.teamMatches) return undefined;

  const id = gameInfo.teamMatches[teamIndex].team.id;
  const lastGameResult = gameInfo.championship?.championshipTable.rows.find((row) => row.team.id === id)?.lastGamesResults;

  return {
    id: gameInfo.teamMatches[teamIndex]?.team?.id,
    name: gameInfo.teamMatches[teamIndex]?.team?.name,
    logo: gameInfo.teamMatches[teamIndex]?.team?.emblem?.externalUrl,
    lastGamesResults: lastGameResult ?? [],
    members: extractMembers(gameInfo, teamIndex),
    score: 0,
    fouls: 0,
  };
}

function extractMembers(gameInfo: IGameInfoDto, teamIndex: number): Member[] {
  if (!gameInfo.teamMatches) return [];

  const championshipId = gameInfo.championship?.id;
  const members = gameInfo.teamMatches[teamIndex].team.championshipApplications;
  const filteredMembers: Member[] = [];

  for (const member of members) {
    if (member.championship.id !== championshipId) continue;
    if (member.state !== 'ACCEPTED') continue;
    filteredMembers.push({
      id: member.user.id,
      name: member.user.name,
      surname: member.user.surname,
      middleName: member.user.middleName ?? '',
      photo: getPhoto(gameInfo, member.user.id, teamIndex),
      position: member.position,
      number: member.number,
      isInvolved: isInvolved(gameInfo, member.user.id),
      qtyGoalsMatch: 0,
      qtyYellowCardsMatch: 0,
      qtyRedCardsMatch: 0,
      qtyFoulsMatch: 0,
      qtyYellowCardsChampionship: 0,
      qtyRedCardsChampionship: 0,
      qtyGoalsChampionship: 0,
    });
  }

  return filteredMembers;
}

function isInvolved(gameInfo: IGameInfoDto, memberId: number) {
  const member = gameInfo.participants?.find((participant) => participant.user.id === memberId);
  if (!member) return false;
  return member.isInvolved;
}

function getPhoto(gameInfo: IGameInfoDto, memberId: number, teamIndex: number) {
  if (!gameInfo.teamMatches) return USER_DEFAULT_AVATAR;
  const member = gameInfo.teamMatches[teamIndex].team.members.find((member) => member.user.id === memberId);
  if (!member) return USER_DEFAULT_AVATAR;
  return member.user.photo?.url ?? USER_DEFAULT_AVATAR;
}

function getStats(
  champStats: ChampionshipStats[],
  memberId: number,
): {
  qtyYellowCardsChampionship: number;
  qtyRedCardsChampionship: number;
  qtyGoalsChampionship: number;
} {
  const stats = champStats;
  if (!stats) return { qtyYellowCardsChampionship: 0, qtyRedCardsChampionship: 0, qtyGoalsChampionship: 0 };
  const memberStats = stats.find((stat) => stat.user.id === memberId);
  if (!memberStats) return { qtyYellowCardsChampionship: 0, qtyRedCardsChampionship: 0, qtyGoalsChampionship: 0 };
  return {
    qtyYellowCardsChampionship: memberStats.yellowcards,
    qtyRedCardsChampionship: memberStats.redcards,
    qtyGoalsChampionship: memberStats.goals,
  };
}

function extractOrganizer(gameInfo: IGameInfoDto): Organizer | undefined {
  if (!gameInfo.tournament) return undefined;

  return {
    name: gameInfo.tournament?.name ?? 'Неизвестно',
    logo: gameInfo.tournament?.images?.[0]?.externalUrl,
    id: gameInfo.tournament?.id,
  };
}

function extractChampionship(gameInfo: IGameInfoDto): Championship | undefined {
  if (!gameInfo.championship) return undefined;

  const overlayIdStr = gameInfo.championship.settings.find((setting) => setting.name === 'overlay_id')?.value;

  return {
    id: gameInfo.championship.id,
    name: gameInfo.championship.name,
    settings: gameInfo.championship.settings,
    overlayId: overlayIdStr ? +overlayIdStr : null,
  };
}
